import Dexie, { type EntityTable } from 'dexie'
import type {
  CategoryRow,
  DaySummaryRow,
  EntryRow,
  EventObjectRow,
  EventTypeRow,
  GoalRow,
  ObjectGroupRow,
  ReminderRow,
  SettingsRow,
  StamperRow,
  SyncStateRow,
} from './types'

/**
 * The local database. IndexedDB is the source of truth for the UI; the server
 * is a sync hub and a backup. See 02-data-model.md section 1.
 *
 * Indexes exist for two purposes:
 *   - reading:  dayKey and [dayKey+slotIndex] drive the timeline
 *   - syncing:  _dirty finds unpushed rows; updatedAt supports merges
 */
export class BlockyTimeDB extends Dexie {
  categories!: EntityTable<CategoryRow, 'id'>
  eventTypes!: EntityTable<EventTypeRow, 'id'>
  objectGroups!: EntityTable<ObjectGroupRow, 'id'>
  eventObjects!: EntityTable<EventObjectRow, 'id'>
  entries!: EntityTable<EntryRow, 'id'>
  stampers!: EntityTable<StamperRow, 'id'>
  reminders!: EntityTable<ReminderRow, 'id'>
  goals!: EntityTable<GoalRow, 'id'>
  daySummaries!: EntityTable<DaySummaryRow, 'id'>
  settings!: EntityTable<SettingsRow, 'id'>
  syncState!: EntityTable<SyncStateRow, 'key'>

  /**
   * The IndexedDB database name is `blockytime` and MUST NOT be changed.
   *
   * The app was renamed to Time Keeper, and this deliberately was not. A Dexie
   * database is identified by its name, so renaming it does not migrate
   * anything — it opens a different, empty database, and every block ever
   * tracked on that device becomes unreachable. It would look exactly like the
   * app had wiped itself.
   *
   * The name is not visible anywhere in the UI, so there is nothing to gain and
   * a year of history to lose. If it ever genuinely has to change, it needs an
   * explicit copy-then-delete migration, not a renamed default.
   */
  constructor(name = 'blockytime') {
    super(name)

    this.version(1).stores({
      categories: 'id, sortOrder, isArchived, updatedAt, _dirty',
      eventTypes: 'id, categoryId, sortOrder, isPinned, isArchived, updatedAt, _dirty',
      objectGroups: 'id, sortOrder, isArchived, updatedAt, _dirty',
      eventObjects: 'id, groupId, sortOrder, isArchived, updatedAt, _dirty',

      // [dayKey+slotIndex] is the compound index behind the slot invariant
      // check, which runs on every single mark. It must be fast.
      entries:
        'id, dayKey, [dayKey+slotIndex], startAt, eventTypeId, eventObjectId, updatedAt, _dirty',

      stampers: 'id, sortOrder, updatedAt, _dirty',
      reminders: 'id, stamperId, isEnabled, updatedAt, _dirty',
      goals: 'id, isArchived, updatedAt, _dirty',
      daySummaries: 'id, dayKey, updatedAt, _dirty',
      settings: 'id, updatedAt, _dirty',

      // Local only, never synced. Holds deviceId, lastPulledSeq, syncToken.
      syncState: 'key',
    })
  }
}

export const db = new BlockyTimeDB()

/**
 * Fresh in-memory database for tests. Each call gets its own IndexedDB name,
 * which is what lets the Phase 4 sync tests run two independent "devices"
 * side by side in one process.
 */
let testDbCounter = 0
export function createTestDb(): BlockyTimeDB {
  testDbCounter += 1
  return new BlockyTimeDB(`blockytime-test-${testDbCounter}-${Date.now()}`)
}
